import { FC, useRef, useState } from 'react'
import { Button } from 'reactstrap'
import { FightSegment } from 'web/data/models'
import { useInputValue } from 'web/hooks/useInputValue'
import { setFocusToRef } from 'web/utils/jsx-utils'
import { secondsToTimestamp, timestampToSeconds, validTimestampOrDefault } from 'web/utils/time-utils'
import { onEnter } from 'web/utils/utils'
import { LabeledTextInput } from '../generic/LabeledTextInput'
import { RadioGroup } from '../generic/RadioGroup'

export type AddNewEventCallback = (params: {
    segment: FightSegment
    name: string
    seconds: number
    relativeTo: "fight" | "segment"
}) =>
    {
        /**
         * Can contain a validation error message
         */
        errorMessage: string
    }
    | undefined

export const AddEventInputs: FC<{
    segment: FightSegment
    onAdd: AddNewEventCallback
}> = ({ segment, onAdd }) => {

    const inputRef = useRef<HTMLInputElement>(null)

    const [isRelativeToFightStart, setIsRelativeToFightStart] = useState(true)
    const segmentTimestamp = secondsToTimestamp(segment.offset)

    const [name, setName] = useState("")

    const [tsError, setTsError] = useState("")
    const [tsValue, tsOnChange, tsOnBlur, tsSetValue] = useInputValue(
        "",
        {
            onBlur: (value) => {
                return validTimestampOrDefault(value, "")
            }
        }
    )

    const handleAdd = () => {
        if (tsValue === "") {
            setTsError("Enter a timestamp")
            setFocusToRef(inputRef)
            return
        }

        const seconds = timestampToSeconds(tsValue)
        const relativeTo = isRelativeToFightStart ? "fight" : "segment"

        const error = onAdd({ segment, name, seconds, relativeTo })

        if (error) {
            setTsError(error.errorMessage)
            setFocusToRef(inputRef)
            return
        }

        setTsError("")
        tsSetValue("")
        setName("")

        setFocusToRef(inputRef)
    }

    return (
        <div className="pb-3 d-flex flex-column bg-light">

            <div className="mt-2 px-3 d-flex space-mx-3 align-items-end">
                <LabeledTextInput
                    label={`Timestamp`}
                    placeholder="Ex: 01:30"
                    style={{ maxWidth: "120px" }}
                    small
                    value={tsValue}
                    onChange={tsOnChange}
                    onBlur={tsOnBlur}
                    innerRef={inputRef}
                    invalid={tsError !== ""}
                />

                <LabeledTextInput
                    label={`Name (optional)`}
                    placeholder="Ex: Frenzy #5"
                    className="flex-grow-1"
                    small
                    value={name}
                    onChange={e => setName(e.target.value)}
                    onKeyPress={onEnter(handleAdd)}
                />

                <Button
                    outline
                    size="sm"
                    className="px-4"
                    onClick={handleAdd}
                >
                    {`Add`}
                </Button>
            </div>

            {
                tsError &&
                <div className="mt-1 px-3 small text-danger">
                    {tsError}
                </div>
            }

            {
                segment.userCreated &&
                <RadioGroup
                    className="mt-2 px-3"
                    selectedValue={(isRelativeToFightStart ? "fight" : "segment")}
                    items={[
                        {
                            label: "Timestamp is relative to fight start (00:00)",
                            value: "fight",
                            onSelected: () => {
                                if (tsValue !== "") {
                                    const offsetSeconds = timestampToSeconds(tsValue)
                                    tsSetValue(secondsToTimestamp(offsetSeconds + segment.offset))
                                }

                                setIsRelativeToFightStart(true)
                            }
                        },
                        {
                            label: `Timestamp is relative to segment start (${segmentTimestamp})`,
                            value: "segment",
                            onSelected: () => {
                                if (tsValue !== "") {
                                    const offsetSeconds = timestampToSeconds(tsValue)
                                    tsSetValue(secondsToTimestamp(offsetSeconds - segment.offset))
                                }

                                setIsRelativeToFightStart(false)
                            }
                        }
                    ]} />
            }

        </div >
    )
}
